import React, { useEffect, useState } from 'react';
import { householdMembers, dietaryProtocols } from '../api/client';

// Manages everyone in the current kitchen: the people who log in plus
// the kids/guests/family members who don't. Each member has its own
// dietary profile (protocol, hard avoids, soft avoids, etc.) which
// drives the AI chat's recipe filtering for whoever's eating.
export default function HouseholdMembersPage() {
  const [members, setMembers] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // member id currently editing
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([householdMembers.list(), dietaryProtocols.list()])
      .then(([m, p]) => {
        setMembers(m.data.household_members || []);
        setProtocols(p.data.dietary_protocols || []);
        setError(null);
      })
      .catch((err) => setError(err.response?.data?.errors?.join(', ') || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = (member) => {
    if (!confirm(`Remove ${member.display_name} from this kitchen?`)) return;
    householdMembers.delete(member.id)
      .then(load)
      .catch((err) => setError(err.response?.data?.errors?.join(', ') || err.message));
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-2xl font-bold">Household members</h1>
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          + Add member
        </button>
      </div>
      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">{error}</div>}

      <p className="mb-4 text-sm text-gray-600">
        Everyone whose meals you track. Add a member for each person — kids and family without logins
        included. Each member's dietary profile drives the AI chat's recipe filtering when they're eating.
      </p>

      {creating && (
        <MemberForm
          protocols={protocols}
          onSubmit={(data) => {
            householdMembers.create(data)
              .then(() => { setCreating(false); load(); })
              .catch((err) => setError(err.response?.data?.errors?.join(', ') || err.message));
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.id} className="border border-gray-200 rounded bg-white">
            <div className="flex items-center justify-between p-3">
              <div>
                <div className="font-medium">{m.display_name}</div>
                <div className="text-xs text-gray-500">
                  {m.user_email ? `Logged-in user: ${m.user_email}` : 'No login'}
                  {m.dietary_protocol_name && ` · ${m.dietary_protocol_name}`}
                </div>
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                  className="text-sm text-indigo-600 hover:underline"
                >
                  {expanded === m.id ? 'Close' : 'Edit'}
                </button>
                <button
                  onClick={() => handleDelete(m)}
                  className="text-sm text-red-600 hover:underline"
                >
                  Remove
                </button>
              </div>
            </div>
            {expanded === m.id && (
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <MemberEditor
                  member={m}
                  protocols={protocols}
                  onSaved={() => { setExpanded(null); load(); }}
                  onCancel={() => setExpanded(null)}
                />
              </div>
            )}
          </div>
        ))}
        {members.length === 0 && !creating && (
          <div className="p-6 text-center text-gray-500 border border-dashed border-gray-300 rounded">
            No household members yet. Add yourself to get started.
          </div>
        )}
      </div>
    </div>
  );
}

function MemberForm({ protocols, onSubmit, onCancel }) {
  const [displayName, setDisplayName] = useState('');
  const [linkToCurrentUser, setLinkToCurrentUser] = useState(false);
  const [protocolId, setProtocolId] = useState('');
  const [hardAvoids, setHardAvoids] = useState('');

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      display_name: displayName,
      link_to_current_user: linkToCurrentUser ? 'true' : 'false',
      dietary_protocol_id: protocolId || null,
      hard_avoids: hardAvoids ? hardAvoids.split(',').map((s) => s.trim()).filter(Boolean) : [],
    });
  };

  return (
    <form onSubmit={submit} className="mb-4 p-4 border border-indigo-200 bg-indigo-50 rounded">
      <h2 className="font-medium mb-3">New member</h2>
      <div className="space-y-3">
        <input
          type="text"
          required
          placeholder="Display name (e.g. Theo)"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
        <label className="flex items-center space-x-2">
          <input type="checkbox" checked={linkToCurrentUser} onChange={(e) => setLinkToCurrentUser(e.target.checked)} />
          <span className="text-sm">Link this member to my login</span>
        </label>
        <select value={protocolId} onChange={(e) => setProtocolId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded">
          <option value="">— No dietary protocol —</option>
          {protocols.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
        </select>
        <input
          type="text"
          placeholder="Hard avoids (comma-separated, e.g. peanuts, shellfish)"
          value={hardAvoids}
          onChange={(e) => setHardAvoids(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
      </div>
      <div className="mt-3 space-x-2">
        <button type="submit" className="px-3 py-1 bg-indigo-600 text-white rounded">Create</button>
        <button type="button" onClick={onCancel} className="px-3 py-1 border border-gray-300 rounded">Cancel</button>
      </div>
    </form>
  );
}

function MemberEditor({ member, protocols, onSaved, onCancel }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    householdMembers.get(member.id).then((res) => setData(res.data.household_member));
  }, [member.id]);

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    householdMembers.update(member.id, {
      display_name: data.display_name,
      dietary_protocol_id: data.dietary_protocol_id || null,
      phase: data.phase || null,
      notes: data.notes,
      hard_avoids: arrify(data.hard_avoids),
      soft_avoids: arrify(data.soft_avoids),
      preferred_cuisines: arrify(data.preferred_cuisines),
      preferred_textures: arrify(data.preferred_textures),
    })
      .then(onSaved)
      .catch((e) => setErr(e.response?.data?.errors?.join(', ') || e.message))
      .finally(() => setSaving(false));
  };

  if (!data.id) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <form onSubmit={save} className="space-y-3">
      {err && <div className="p-2 bg-red-50 text-red-700 rounded">{err}</div>}
      <Field label="Display name">
        <input type="text" value={data.display_name || ''} onChange={(e) => setData({ ...data, display_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded" />
      </Field>
      <Field label="Dietary protocol">
        <select value={data.dietary_protocol_id || ''} onChange={(e) => setData({ ...data, dietary_protocol_id: e.target.value || null })} className="w-full px-3 py-2 border border-gray-300 rounded">
          <option value="">— None —</option>
          {protocols.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
        </select>
      </Field>
      <Field label="Hard avoids (comma-separated, medical/blocking)">
        <input type="text" value={listToText(data.hard_avoids)} onChange={(e) => setData({ ...data, hard_avoids: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded" />
      </Field>
      <Field label="Soft avoids (preferences, strong suggestion not blocking)">
        <input type="text" value={listToText(data.soft_avoids)} onChange={(e) => setData({ ...data, soft_avoids: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded" />
      </Field>
      <Field label="Preferred cuisines">
        <input type="text" value={listToText(data.preferred_cuisines)} onChange={(e) => setData({ ...data, preferred_cuisines: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded" />
      </Field>
      <Field label="Notes">
        <textarea value={data.notes || ''} onChange={(e) => setData({ ...data, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded" rows="2" />
      </Field>
      <div className="space-x-2">
        <button type="submit" disabled={saving} className="px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1 border border-gray-300 rounded">Cancel</button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function listToText(value) {
  if (Array.isArray(value)) return value.join(', ');
  return value || '';
}

function arrify(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}
